import { TextWithVariants } from "@landtechnologies/components";
import { MultiPolygon } from "geojson";
import { useMemo } from "react";
import {
  ImperialArea,
  ImperialPerimeter,
  MetricArea,
  MetricPerimeter,
} from "react-migration/lib/util/MeasurementTypes";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Stores, useGlobalState } from "react-migration/lib/persistence/state";
import { calculateArea, calculatePerimeter } from "react-migration/lib/util/geometry";
import { MeasurementSystem } from "src/js/stores/user/store";

interface DimensionsProps {
  geometry: GeoJSON.Polygon | MultiPolygon;
}

function useDimensions(geometry: GeoJSON.Polygon | MultiPolygon) {
  const [, { getMeasurementSystemPreference }] = useGlobalState(Stores.user);

  const areaVariants = useMemo(() => {
    if (!geometry) {
      return [];
    }
    return getMeasurementSystemPreference() === MeasurementSystem.IMPERIAL
      ? [calculateArea(geometry, ImperialArea.SQ_FEET), calculateArea(geometry, ImperialArea.ACRE)]
      : [calculateArea(geometry, MetricArea.SQ_METER), calculateArea(geometry, MetricArea.HECTARE)];
  }, [geometry, getMeasurementSystemPreference()]);

  const perimeterVariants = useMemo(() => {
    if (!geometry) {
      return [];
    }
    return getMeasurementSystemPreference() === MeasurementSystem.IMPERIAL
      ? [
          calculatePerimeter(geometry, ImperialPerimeter.FEET),
          calculatePerimeter(geometry, ImperialPerimeter.MILES),
        ]
      : [
          calculatePerimeter(geometry, MetricPerimeter.METERS),
          calculatePerimeter(geometry, MetricPerimeter.KILOMETER),
        ];
  }, [geometry, getMeasurementSystemPreference()]);

  return { areaVariants, perimeterVariants };
}

export function Dimensions({ geometry }: DimensionsProps) {
  const { t } = useTranslation();
  const { areaVariants, perimeterVariants } = useDimensions(geometry);

  return (
    <>
      <div className="atlas-flex atlas-justify-between atlas-pb-3 atlas-text-sm ">
        <span className="atlas-font-medium atlas-text-neutral-600">
          {t("sites.card.summary.area_label")}
        </span>
        <TextWithVariants data-testid="site-area" variants={areaVariants} />
      </div>
      <div className="atlas-flex atlas-justify-between atlas-pt-3 atlas-pb-3 atlas-text-sm">
        <span className="atlas-font-medium atlas-text-neutral-600">
          {t("sites.card.summary.perimeter_label")}
        </span>
        <TextWithVariants data-testid="site-perimeter" variants={perimeterVariants} />
      </div>
    </>
  );
}
